import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AuthenticationService,AppConfigService } from '@alfresco/adf-core';

// ── DTOs ──────────────────────────────────────────────────────────────────

export interface MailTaskDto {
  taskId: string;
  taskType: string;          // DISPATCH | INBOX | DRAFT
  documentNodeId: string;
  referenceNumber: string;
  subject: string;
  mailType: string;          // INCOMING | OUTGOING | INTERNAL_MEMO
  fileClassCode: string;
  priority: string;          // NORMAL | URGENT | CONFIDENTIAL
  fromName: string;
  fromOrganization: string;
  createdAt: string;
  dueDate?: string;
  overdue: boolean;
  currentStage: string;      // DRAFT | DISPATCH | INBOX | BRINGUP | ARCHIVED
  status?: string;           // DRAFT | ACTIVE | COMPLETED
  assignee: string;
  assigneeName: string;
  myTask: boolean;
  isSplitTask: boolean;
  pendingSplits: number;
}

export interface MailWorkflowStatusResponse {
  workflowStarted: boolean;
  processInstanceId?: string;
  documentNodeId?: string;
  referenceNumber?: string;
  subject?: string;
  currentStage?: string;
  currentAssignee?: string;
  currentAssigneeName?: string;
  status?: string;
  activeTaskId?: string;
  myTask: boolean;
  startedAt?: string;
  completedAt?: string;
}

export interface MailParticipantDto {
  role: string;
  username: string;
  displayName: string;
  email?: string;
  groupId?: string;
  groupName?: string;
  instructions?: string;
  status: string;
  actionTaken?: string;
  assignedAt?: string;
  actedAt?: string;
  assignedBy?: string;
}

export interface MailCommentDto {
  id: number;
  authorUsername: string;
  authorName: string;
  content: string;
  createdAt: string;
  parentId?: number;
  replies: MailCommentDto[];
}

export interface MailAuditDto {
  action: string;
  actorUsername: string;
  actorName?: string;
  fromStage?: string;
  toStage?: string;
  fromAssignee?: string;
  toAssignee?: string;
  note?: string;
  actionTimestamp: string;
}

export interface SplitTaskDto {
  id: number;
  splitTaskId: string;
  assigneeUsername: string;
  assigneeName: string;
  instructions?: string;
  deadlineAt?: string;
  status: string;
  completedAt?: string;
}

export interface BringupDto {
  id: number;
  bringupType: string;      // DATE | CONDITION
  bringupDate?: string;
  conditionNote?: string;
  triggeredBy: string;
  createdAt: string;
}

export interface RelatedMailDto {
  id: number;
  documentNodeId: string;
  referenceNumber: string;
  subject: string;
  mailType: string;
  createdBy: string;
  createdAt: string;
}


export interface MailTaskDetailResponse {
  taskId: string;
  taskType: string;
  taskName: string;
  assignee: string;
  assigneeName: string;
  createdAt: string;
  dueDate?: string;
  overdue: boolean;
  myTask: boolean;
  documentNodeId: string;
  referenceNumber: string;
  subject: string;
  mailType: string;
  fileClassCode: string;
  fileClassName: string;
  priority: string;
  fromName: string;
  fromOrganization: string;
  fileReferenceId?: number;      // FK to swf_file_reference.id
  fileReferenceName?: string;
  fileReferenceNo?: string;
  fromEmail: string;
  dateOfMail: string;
  externalReference: string;
  source: string;
  currentStage: string;
  processInstanceId: string;
  status: string;            // DRAFT | ACTIVE | COMPLETED
  deadlineAt?: string;
  escalateTo?: string;
  escalateToName?: string;
  participants: MailParticipantDto[];
  comments: MailCommentDto[];
  auditTrail: MailAuditDto[];
  relatedDocuments: RelatedMailDto[];
  splitTasks: SplitTaskDto[];
  activeBringup?: BringupDto;
  fileClassId?: number;
  refYear: number;
  refMonth: number;
  instructions?: string;
}

export interface FileClassDto {
  id: number;
  code: string;
  name: string;
  description?: string;
}

export interface MailTaskCounts {
  DISPATCH: number;
  INBOX:    number;
  BRINGUP:  number;
  DRAFTS:   number;
}


// ── File Reference ────────────────────────────────────────────────────────
export interface FileReferenceDto {
  id: number;
  name: string;
  referenceNo: string;
  fileClassId: number;
}

export interface CreateFileReferenceRequest {
  fileClassId: number;
  name: string;
  referenceNo: string;
}

// ── NEW: Draft / Initiate DTOs ───────────────────────────────────────────

export interface MailInitiateRequest {
  mailType: 'INCOMING' | 'OUTGOING' | 'INTERNAL_MEMO';
  mailSource: 'SCAN' | 'UPLOAD' | 'MEMO';  // derived from mailType — never shown in UI
  documentNodeId: string;        // Alfresco nodeId — uploaded in step 2
  subject: string;
  fromOrganization?: string;
  fromName?: string;
  fromEmail?: string;
  externalReference?: string;
  dateOfMail?: string;           // ISO date string
  dateReceived?: string;
  fileClassId: number;
  fileReferenceId?: number;       // id of FileReference (the docket under this class)
  folio?: number;                  // document folio number within that file
  priority: 'NORMAL' | 'URGENT' | 'CONFIDENTIAL';
  // Recipient — Step 5 "Send To"
  dispatchTo?: string;           // Alfresco username
  dispatchToGroup?: string;      // Alfresco group id e.g. GROUP_HR
  // Optional dispatch fields
  deadlineAt?: string;           // ISO datetime string
  escalateTo?: string;           // username to escalate to if overdue
  instructions?: string;         // notes to recipient
}

export interface MailDraftResponse {
  draftId: string;
  referenceNumber: string;
  documentNodeId: string;
  subject: string;
  status: 'DRAFT';
}

export interface MailUploadResponse {
  nodeId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
}

// ── NEW: Template DTO ────────────────────────────────────────────────────

export interface MailTemplateDto {
  id: string;               // Alfresco nodeId of the template file
  name: string;             // e.g. "General Memo"
  category: string;         // GENERAL | HR | FINANCE | LEGAL
  description: string;
  thumbnailUrl?: string;
  documentTypeKey?: string; // bound document type (when known to the picker)
}

export interface MemoFieldOption { value: string; label: string; }

/** One dynamically-rendered memo wizard field (from the document type config). */
export interface MemoFieldDto {
  propertyName: string;
  label: string;
  type: string;             // text | date | number | select | textarea | boolean
  mandatory?: boolean;
  placeholder?: string;
  defaultValue?: string;
  autoPopulateDate?: boolean;
  autoPopulateUserId?: boolean;
  options?: MemoFieldOption[];
  refList?: string;
  /** Feature 2/7 — render a repeatable "+Add" input (e.g. multiple "Through"). */
  multiValue?: boolean;
  /** person/personOrRole fields — limit the user search to these realm role(s) (CSV; blank = everyone). */
  restrictRoles?: string;
}

export interface MemoFieldsResponse {
  documentTypeKey?: string;
  fields: MemoFieldDto[];
}

export interface MailMemoCreateResponse {
  nodeId: string;           // new Alfresco node (copy of template)
  draftId: string;          // draft workflow state id
  referenceNumber: string;
  onlyOfficeUrl: string;    // pre-built OO editor URL
}

// ── Service ───────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class MailWorkflowService {

  private readonly base: string;
  //private readonly base = `${environment.apiUrl}/mail/workflow`;

  // Annotator viewer URL - populated from app.config.json annotatorUrl
  // (full viewer URL, e.g. "http://localhost:8092/annotator/viewer")
  private readonly annotatorUrl: string;

  readonly taskCounts$ = new BehaviorSubject<MailTaskCounts>({ DISPATCH: 0, INBOX: 0, BRINGUP: 0, DRAFTS: 0 });

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService,
    private appConfig: AppConfigService
  ) {
    const apiBase = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    this.base = `${apiBase}/mail/workflow`;

    // Annotator URL — falls back to the dev default if the key is missing.
    let annotatorUrl = this.appConfig.get<string>('annotatorUrl', '')
      || 'http://localhost:8092/annotator/viewer';
    if (annotatorUrl.endsWith('/')) {
      annotatorUrl = annotatorUrl.slice(0, -1);
    }
    this.annotatorUrl = annotatorUrl;
  }

  private getHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    return new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' });
  }

  private getBearerHeader(): HttpHeaders {
    const token = this.authService.getToken();
    return new HttpHeaders({ 'Authorization': `Bearer ${token}` });
  }

  // ── Workflow lifecycle ───────────────────────────────────────────────────

  startWorkflow(request: MailInitiateRequest): Observable<MailWorkflowStatusResponse> {
    return this.http.post<any>(`${this.base}/start`, request, { headers: this.getHeaders() })
      .pipe(map(r => r.workflow), tap(() => this.refreshTaskCounts()));
  }

  getWorkflowStatus(documentNodeId: string): Observable<MailWorkflowStatusResponse> {
    return this.http.get<any>(`${this.base}/${documentNodeId}/status`, { headers: this.getHeaders() })
      .pipe(map(r => r.workflow));
  }

  // ── Document upload ──────────────────────────────────────────────────────
  // Uploads to Alfresco immediately (step 2 of wizard). Returns nodeId.
  // Backend: POST /api/mail/workflow/upload (multipart/form-data, field: "file")

  uploadDocument(file: File): Observable<MailUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(`${this.base}/upload`, formData, { headers: this.getBearerHeader() })
      .pipe(map(r => r.upload));
  }

  // ── Draft CRUD ───────────────────────────────────────────────────────────
  // Backend: POST /api/mail/workflow/draft  → saves metadata + nodeId, no Flowable process
  // Returns draftId and auto-generated referenceNumber

  saveDraft(request: MailInitiateRequest): Observable<MailDraftResponse> {
    return this.http.post<any>(`${this.base}/draft`, request, { headers: this.getHeaders() })
      .pipe(map(r => r.draft), tap(() => this.refreshTaskCounts()));
  }

  // Backend: POST /api/mail/workflow/draft/{draftId}/submit → starts Flowable
  submitDraft(draftId: string): Observable<MailWorkflowStatusResponse> {
    return this.http.post<any>(`${this.base}/draft/${draftId}/submit`, {}, { headers: this.getHeaders() })
      .pipe(map(r => r.workflow), tap(() => this.refreshTaskCounts()));
  }

  // Backend: DELETE /api/mail/workflow/draft/{draftId}
  deleteDraft(draftId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/draft/${draftId}`, { headers: this.getHeaders() })
      .pipe(tap(() => this.refreshTaskCounts()));
  }

  // Backend: GET /api/mail/workflow/my-drafts
  getMyDrafts(): Observable<MailTaskDto[]> {
    return this.http.get<any>(`${this.base}/my-drafts`, { headers: this.getHeaders() })
      .pipe(map(r => (r.drafts ?? r.tasks ?? []).map((d: any) => ({ ...d, taskId: d.taskId || d.draftId }))));
  }

  // ── Internal Memo templates ──────────────────────────────────────────────
  // Backend: GET /api/mail/workflow/templates?category=all
  // Templates are .docx files stored in Alfresco /Templates/Mail/

  getTemplates(category?: string): Observable<MailTemplateDto[]> {
    let params = new HttpParams();
    if (category && category !== 'ALL') params = params.set('category', category);
    return this.http.get<any>(`${this.base}/templates`, { headers: this.getHeaders(), params })
      .pipe(map(r => r.templates));
  }

  // Backend: POST /api/mail/workflow/memo/create
  // Fills the template with captured indexValues, copies it to a new Alfresco
  // location, stamps the values as aspect properties, returns the new nodeId +
  // a draft + the OO editor URL.
  createMemoFromTemplate(templateNodeId: string, metadata: {
    subject: string;
    fileClassId: number;
    priority: string;
    dateOfMail?: string;
    documentTypeKey?: string;
    indexValues?: Record<string, any>;
  }): Observable<MailMemoCreateResponse> {
    return this.http.post<any>(`${this.base}/memo/create`,
      { templateNodeId, ...metadata }, { headers: this.getHeaders() })
      .pipe(map(r => ({
        nodeId:          r.memo?.documentNodeId,
        draftId:         r.memo?.draftId,
        referenceNumber: r.memo?.referenceNumber ?? '',
        onlyOfficeUrl:   r.memo?.onlyOfficeConfigUrl ?? ''
      })));
  }

  // Backend: GET /api/mail/workflow/memo/fields
  // Returns the document type's memo-stage fields for the chosen template.
  getMemoFields(templateNodeId: string, documentTypeKey?: string): Observable<MemoFieldsResponse> {
    let params = new HttpParams();
    if (templateNodeId)  params = params.set('templateNodeId', templateNodeId);
    if (documentTypeKey) params = params.set('documentTypeKey', documentTypeKey);
    return this.http.get<any>(`${this.base}/memo/fields`, { headers: this.getHeaders(), params })
      .pipe(map(r => ({ documentTypeKey: r.documentTypeKey, fields: r.fields ?? [] })));
  }

  // ── Task lists ───────────────────────────────────────────────────────────

  getMyTasks(type?: string): Observable<MailTaskDto[]> {
    let params = new HttpParams();
    if (type) params = params.set('type', type);
    return this.http.get<any>(`${this.base}/my-tasks`, { headers: this.getHeaders(), params })
      .pipe(map(r => (r.drafts ?? r.tasks ?? []).map((d: any) => ({ ...d, taskId: d.taskId || d.draftId }))));
  }

  getInitiatedWorkflows(): Observable<MailTaskDto[]> {
    return this.http.get<any>(`${this.base}/my-initiated`, { headers: this.getHeaders() })
      .pipe(map(r => (r.drafts ?? r.tasks ?? []).map((d: any) => ({ ...d, taskId: d.taskId || d.draftId }))));
  }

  // Backend: GET /api/mail/workflow/my-forwarded
  getForwardedItems(): Observable<MailTaskDto[]> {
    return this.http.get<any>(`${this.base}/my-forwarded`, { headers: this.getHeaders() })
      .pipe(map(r => (r.drafts ?? r.tasks ?? []).map((d: any) => ({ ...d, taskId: d.taskId || d.draftId }))));
  }

  // Backend: GET /api/mail/workflow/my-archived
  getArchivedItems(): Observable<MailTaskDto[]> {
    return this.http.get<any>(`${this.base}/my-archived`, { headers: this.getHeaders() })
      .pipe(map(r => (r.drafts ?? r.tasks ?? []).map((d: any) => ({ ...d, taskId: d.taskId || d.draftId }))));
  }

  // Backend: GET /api/mail/workflow/my-bringups
  getBringupItems(): Observable<MailTaskDto[]> {
    return this.http.get<any>(`${this.base}/my-bringups`, { headers: this.getHeaders() })
      .pipe(map(r => (r.drafts ?? r.tasks ?? []).map((d: any) => ({ ...d, taskId: d.taskId || d.draftId }))));
  }

  refreshTaskCounts(): void {
    this.http.get<any>(`${this.base}/my-tasks/counts`, { headers: this.getHeaders() })
      .pipe(map(r => r.counts))
      .subscribe({ next: c => this.taskCounts$.next(c), error: e => console.error(e) });
  }

  // ── Task detail & completion ─────────────────────────────────────────────

  getTaskDetail(taskId: string): Observable<MailTaskDetailResponse> {
    return this.http.get<any>(`${this.base}/task/${taskId}`, { headers: this.getHeaders() })
      .pipe(map(r => r.task));
  }

  completeTask(taskId: string, request: any): Observable<MailWorkflowStatusResponse> {
    return this.http.post<any>(`${this.base}/task/${taskId}/complete`, request, { headers: this.getHeaders() })
      .pipe(map(r => r.workflow), tap(() => this.refreshTaskCounts()));
  }

  // ── Comments ─────────────────────────────────────────────────────────────

  getComments(documentNodeId: string, taskId: string): Observable<MailCommentDto[]> {
    return this.http.get<any>(`${this.base}/task/${taskId}/comments`,
      { headers: this.getHeaders(), params: new HttpParams().set('documentNodeId', documentNodeId) })
      .pipe(map(r => r.comments));
  }

  addComment(taskId: string, documentNodeId: string, content: string, parentId?: number): Observable<MailCommentDto> {
    return this.http.post<any>(`${this.base}/task/${taskId}/comment`,
      { documentNodeId, content, parentId }, { headers: this.getHeaders() })
      .pipe(map(r => r.comment));
  }

  // ── Audit ────────────────────────────────────────────────────────────────

  getAuditTrail(documentNodeId: string): Observable<MailAuditDto[]> {
    return this.http.get<any>(`${this.base}/${documentNodeId}/audit`, { headers: this.getHeaders() })
      .pipe(map(r => r.trail));
  }

  // ── Bring-up ─────────────────────────────────────────────────────────────

  releaseBringup(documentNodeId: string): Observable<any> {
    return this.http.post<any>(`${this.base}/${documentNodeId}/bringup/release`, {},
      { headers: this.getHeaders() });
  }

  // ── Split task ───────────────────────────────────────────────────────────

  completeSplitTask(splitTaskId: string, note?: string): Observable<any> {
    return this.http.post<any>(`${this.base}/split-task/${splitTaskId}/complete`,
      { note }, { headers: this.getHeaders() });
  }

  // ── File browser ─────────────────────────────────────────────────────────

  getFileBrowser(fileClassId: number, year: number, month: number): Observable<any[]> {
    const params = new HttpParams()
      .set('fileClassId', fileClassId)
      .set('year', year)
      .set('month', month);
    return this.http.get<any>(`${this.base}/file-browser`, { headers: this.getHeaders(), params })
      .pipe(map(r => r.folios));
  }

  // ── Reference number ─────────────────────────────────────────────────────

  generateRefNumber(fileClassId: number): Observable<string> {
    const params = new HttpParams().set('fileClassId', fileClassId);
    return this.http.get<any>(`${this.base}/ref-number/generate`, { headers: this.getHeaders(), params })
      .pipe(map(r => r.referenceNumber));
  }

  // ── File classes ─────────────────────────────────────────────────────────


  // ── Current user (for pre-filling Sender Name) ────────────────────────
  getCurrentUser(): Observable<{ username: string; displayName: string; email: string }> {
    return this.http.get<any>(`${this.base}/me`, { headers: this.getHeaders() })
      .pipe(map(r => ({ username: r.username, displayName: r.displayName, email: r.email })));
  }

  // ── File References ────────────────────────────────────────────────────
  getFileReferences(fileClassId: number): Observable<FileReferenceDto[]> {
    return this.http.get<any>(`${this.base}/file-class/${fileClassId}/references`, { headers: this.getHeaders() })
      .pipe(map(r => r.references ?? []));
  }

  createFileReference(req: CreateFileReferenceRequest): Observable<FileReferenceDto> {
    return this.http.post<any>(`${this.base}/file-reference`, req, { headers: this.getHeaders() })
      .pipe(map(r => r.reference));
  }

  getFileClasses(): Observable<FileClassDto[]> {
    return this.http.get<any>(`${this.base}/file-classes`, { headers: this.getHeaders() })
      .pipe(map(r => r.fileClasses));
  }

  // ── User & Group search (Alfresco) ────────────────────────────────────────
  // Both use the mail-specific endpoints which use the legacy Alfresco people/groups
  // API — same approach as TemplateService in the contracts package.

  searchUsers(filter: string): Observable<Array<{ id: string; displayName: string; email: string }>> {
    const params = new HttpParams().set('filter', filter).set('maxItems', '20');
    return this.http
      .get<any>(`${this.base}/users/search`, { headers: this.getHeaders(), params })
      .pipe(map(r => r.users ?? []));
  }

  searchGroups(filter: string): Observable<Array<{ id: string; displayName: string }>> {
    const params = new HttpParams().set('filter', filter).set('maxItems', '20');
    return this.http
      .get<any>(`${this.base}/groups/search`, { headers: this.getHeaders(), params })
      .pipe(map(r => r.groups ?? []));
  }

  /**
   * Search assignable "groups" — these are KEYCLOAK realm ROLES (a step bound to a role surfaces to
   * everyone carrying it, so a role is the assignable group in this system). Same source the Ordered
   * Approver / Role picker use. Mapped to {id: role name, displayName} for the picker rows.
   */
  searchRoles(filter: string): Observable<Array<{ id: string; displayName: string; description?: string }>> {
    const params = new HttpParams().set('filter', filter).set('maxItems', '20');
    return this.http
      .get<any>(`${this.base}/roles/search`, { headers: this.getHeaders(), params })
      .pipe(map(r => (r.roles ?? []).map((role: any) => ({
        id: role.id ?? role.name,
        displayName: role.name ?? role.id,
        description: role.description
      }))));
  }
  /**
   * Lists all documents attached to a mail workflow.
   * Backend: GET /api/mail/workflow/{documentNodeId}/documents
   * Returns: { documents: [ { nodeId, name, mimeType, size, createdAt } ] }
   */
  getNodeDocuments(documentNodeId: string): Observable<Array<{
    nodeId: string; name: string; mimeType: string;
    size: number; createdAt: string; isDocx: boolean;
  }>> {
    return this.http
      .get<any>(`${this.base}/${documentNodeId}/documents`, { headers: this.getHeaders() })
      .pipe(map(r => r.documents ?? []));
  }

  /**
   * Upload an additional document tied to an existing workflow.
   * Backend: POST /api/mail/workflow/{documentNodeId}/documents (multipart)
   */
  addDocument(documentNodeId: string, file: File): Observable<{ nodeId: string; name: string }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http
      .post<any>(`${this.base}/${documentNodeId}/documents`, formData, { headers: this.getBearerHeader() })
      .pipe(map(r => r.document));
  }

  /**
   * Returns an Alfresco URL that opens the built-in document preview.
   * Requires the user to have an active Alfresco session (they do — ACA auth).
   */
  getNodePreviewUrl(nodeId: string): string {
    // Alfresco Share document details page
    return `${environment.alfrescoUrl}/share/page/document-details?nodeRef=workspace://SpacesStore/${nodeId}`;
  }

  /**
   * Returns an Alfresco direct content download URL.
   * The Spring backend proxies this with Basic auth.
   * Alternatively open directly: /alfresco/api/.../nodes/{nodeId}/content?attachment=true
   */
  getNodeDownloadUrl(nodeId: string): string {
    return `${this.base}/${nodeId}/content`;
  }

  /** Fetch document as Blob for in-browser preview iframe (avoids auth/X-Frame-Options) */
  getContentBlob(nodeId: string): Observable<Blob> {
    const token = this.authService.getToken();
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}` });
    return this.http.get(`${this.base}/${nodeId}/content`, { headers, responseType: 'blob' });
  }

  /**
   * Returns the next folio number for a file reference.
   * Backend counts existing docs (live + draft) under the same referenceId and returns count + 1.
   */
  getNextFolio(referenceId: number): Observable<number> {
    return this.http.get<any>(`${this.base}/file-reference/${referenceId}/next-folio`,
      { headers: this.getHeaders() })
      .pipe(map(r => r.nextFolio));
  }

  /**
   * Returns all documents filed under a file reference, ordered by folio.
   * Source: swf_mail_workflow_state (pure DB — no Alfresco round-trips).
   */
  getFileFolios(fileReferenceId: number, order: 'asc' | 'desc' = 'asc'): Observable<any[]> {
    return this.http.get<any>(
      `${this.base}/file-reference/${fileReferenceId}/folios?order=${order}`,
      { headers: this.getHeaders() })
      .pipe(map(r => r.folios ?? []));
  }

  // ── All documents in a workflow instance folder ───────────────────────────
  // GET /api/mail/workflow/{documentNodeId}/documents
  getWorkflowDocuments(documentNodeId: string): Observable<Array<{
    nodeId: string; name: string; mimeType: string;
    size: number; createdAt: string; isDocx: boolean;
  }>> {
    return this.http
      .get<any>(`${this.base}/${documentNodeId}/documents`, { headers: this.getHeaders() })
      .pipe(map(r => r.documents ?? []));
  }

  // POST /api/mail/workflow/{documentNodeId}/documents (multipart)
  addWorkflowDocument(documentNodeId: string, file: File): Observable<{ nodeId: string; name: string }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http
      .post<any>(`${this.base}/${documentNodeId}/documents`, formData, { headers: this.getBearerHeader() })
      .pipe(map(r => r.document));
  }

  /**
   * Opens the SkyPlus Annotator in a popup window to sign/annotate a document.
   * Mirrors SkyPlusDocumentLibraryComponent.signDocument() — uses the
   * authenticated ECM username and the Alfresco nodeId to build the URL.
   *
   * Optionally polls popup.closed every 500 ms and fires onClose() when
   * the popup is dismissed, so callers can refresh their state
   * (e.g. reload the inline viewer with the signed copy).
   *
   * @param documentNodeId Alfresco nodeId of the document to sign
   * @param onClose        Optional callback fired when the popup closes
   *                       (signed or cancelled). Omit for fire-and-forget.
   */
  signDocument(documentNodeId: string, onClose?: () => void,
               stampIdentity: boolean = true, signEndpoint: string = ''): void {
    if (!documentNodeId) return;

    const popup = this.openAnnotatorWindow(documentNodeId, stampIdentity, signEndpoint);

    // Fire-and-forget mode (matches doc-library exactly when no callback supplied)
    if (!onClose) return;

    // Callback mode — preserve mail-detail's reload-after-signing behaviour
    if (!popup) {
      // Popup blocked by browser — treat as cancelled and invoke callback
      // so the caller can reset its loading state.
      console.warn('[MailWorkflowService] Signing popup was blocked by the browser.');
      onClose();
      return;
    }

    const interval = setInterval(() => {
      try {
        if (popup.closed) {
          clearInterval(interval);
          onClose();
        }
      } catch (e) {
        // Cross-origin check may throw — popup is definitely closed at this point
        clearInterval(interval);
        onClose();
      }
    }, 500);
  }

  /**
   * Opens the annotator popup. Opens synchronously (so the click gesture is
   * preserved and it isn't popup-blocked) to a loading page, then navigates to a
   * signed viewer URL when the backend has signed-access enabled, otherwise to the
   * legacy ?userId= URL (today's behaviour). Non-breaking: disabled / 404 / any
   * error all fall back to the legacy URL.
   */
  private openAnnotatorWindow(documentNodeId: string, stampIdentity: boolean = true,
                              signEndpoint: string = ''): Window | null {
    const userId = this.authService.getEcmUsername() || 'unknown';
    // Off → tell the annotator (via token + query) not to draw the signer name/date.
    const stampQ = stampIdentity ? '' : '&stampIdentity=false';
    // Per-step annotator endpoint (dynamic); blank = the annotator's default.
    const epClean = (signEndpoint || '').replace(/[^A-Za-z0-9]/g, '');
    const epQ = epClean ? `&signEndpoint=${encodeURIComponent(epClean)}` : '';
    const legacyUrl = `${this.annotatorUrl}?docId=${documentNodeId}&userId=${userId}${stampQ}${epQ}`;
    const features = 'width=1300,height=900,scrollbars=yes,resizable=yes';
    const popup = window.open('', 'SignDocument', features);
    if (popup) {
      try { popup.document.write('<!doctype html><title>Loading…</title><body style="font:14px sans-serif;padding:24px">Loading document…</body>'); } catch { /* ignore */ }
    }
    const go = (url: string) => { if (popup) { popup.location.href = url; } };
    const apiBase = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    this.http.post<any>(`${apiBase}/signing/viewer-url`,
        { docId: documentNodeId, scope: 'annotate', stampIdentity: String(stampIdentity), signEndpoint: epClean },
        { headers: this.getHeaders() })
      .subscribe({
        next: (resp) => go(resp && resp.enabled && resp.token
          ? `${this.annotatorUrl}?docId=${encodeURIComponent(documentNodeId)}&token=${encodeURIComponent(resp.token)}${stampQ}${epQ}`
          : legacyUrl),
        error: () => go(legacyUrl)
      });
    return popup;
  }
}